import React, { ReactElement, useState, useRef, useEffect, useMemo } from 'react';

function dateInputValueToTs(s: string): number {
  const [y, mo, day] = s.split('-').map(Number);
  return new Date(y, mo - 1, day).getTime();
}
import { useLiveQuery } from 'dexie-react-hooks';
import {
  createTodo,
  fromDBPage,
  removePageTodoStatus,
} from '../../db/pages';
import { db } from '../../db/db';
import { logger } from '../../utils/logger';
import {
  Button,
  Checkbox,
  Input,
  Stack,
  Select,
  Option,
  Typography,
  FormControl,
  FormLabel,
} from '@mui/joy';

type SortKey = 'priority' | 'dueDate' | 'modifiedDate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import PageRow from '../flexView/pagelists/pagerow/PageRow';
import Inspector from '../inspector/Inspector';
import SplitPane from 'react-split-pane';
import { clearSelectionAndSelectMulti } from '../../store/uiSlice';
import { useAppDispatch } from '../../store/hooks';

const TodoView = (): ReactElement => {
  const dispatch = useAppDispatch();
  const [showCompleted, setShowCompleted] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('priority');
  const [addingNew, setAddingNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPriority, setNewPriority] = useState<number | null>(null);
  const [newDueDate, setNewDueDate] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const newTitleRef = useRef<HTMLInputElement>(null);

  const todos = useLiveQuery(
    () =>
      showCompleted
        ? db.pages.where('completionStatus').anyOf(['pending', 'completed']).toArray()
        : db.pages.where('completionStatus').equals('pending').toArray(),
    [showCompleted]
  );

  const sorted = useMemo(() => {
    const list = (todos ?? []).slice();
    if (sortKey === 'priority') {
      list.sort((a, b) => {
        const pa = a.priority ?? 999;
        const pb = b.priority ?? 999;
        if (pa !== pb) return pa - pb;
        const ca = a.creationDate ?? 0;
        const cb = b.creationDate ?? 0;
        return cb - ca;
      });
    } else if (sortKey === 'dueDate') {
      list.sort((a, b) => {
        const da = a.dueDate ?? Infinity;
        const db_ = b.dueDate ?? Infinity;
        if (da !== db_) return da - db_;
        const pa = a.priority ?? 999;
        const pb = b.priority ?? 999;
        return pa - pb;
      });
    } else {
      // modifiedDate: most recently created/modified first
      list.sort((a, b) => {
        const ca = a.creationDate ?? 0;
        const cb = b.creationDate ?? 0;
        return cb - ca;
      });
    }
    return list;
  }, [todos, sortKey]);

  useEffect(() => {
    if (addingNew) {
      newTitleRef.current?.focus();
    }
  }, [addingNew]);

  const handleAdd = async () => {
    const title = newTitle.trim();
    if (!title) return;
    const dueDate = newDueDate ? dateInputValueToTs(newDueDate) : undefined;
    const priority = newPriority ?? undefined;
    setAddError(null);
    // Reset form immediately so the user gets instant feedback and can't double-submit
    setNewTitle('');
    setNewPriority(null);
    setNewDueDate('');
    setAddingNew(false);
    try {
      await createTodo(title, priority, dueDate);
    } catch (e: unknown) {
      logger.error('[TodoView] createTodo failed', e);
      setAddError(e instanceof Error ? e.message : 'Failed to create todo');
      // Re-open the form with the original title so the user can retry
      setNewTitle(title);
      setAddingNew(true);
    }
  };

  const handleNewKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      void handleAdd();
    } else if (e.key === 'Escape') {
      setAddingNew(false);
      setNewTitle('');
      setNewPriority(null);
      setNewDueDate('');
      setAddError(null);
    }
  };

  return (
    /* @ts-ignore TS2322 — react-split-pane children type incompatible with React 18 */
    <SplitPane
      allowResize={true}
      split="vertical"
      defaultSize={300}
      minSize={10}
      primary="second"
    >
      <div className="fill flexContainerHorizontal mainPane">
        <div className="flexFillHorizontal" style={{ overflow: 'scroll' }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Button
                size="sm"
                variant="outlined"
                startDecorator={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => setAddingNew(true)}
              >
                Add Todo
              </Button>
              <Checkbox
                size="sm"
                label="Show completed"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
              />
              <FormControl size="sm" orientation="horizontal" sx={{ alignItems: 'center', gap: 1 }}>
                <FormLabel sx={{ mb: 0 }}>Sort by</FormLabel>
                <Select
                  size="sm"
                  value={sortKey}
                  onChange={(_: React.SyntheticEvent | null, val: SortKey | null) => { if (val) setSortKey(val); }}
                  sx={{ minWidth: 130 }}
                >
                  <Option value="priority">Priority</Option>
                  <Option value="dueDate">Due Date</Option>
                  <Option value="modifiedDate">Date Created</Option>
                </Select>
              </FormControl>
            </Stack>

            {addingNew && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                <Input
                  size="sm"
                  placeholder="Todo title..."
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={handleNewKeyDown}
                  slotProps={{ input: { ref: newTitleRef } }}
                  sx={{ flex: 1 }}
                />
                <Select
                  size="sm"
                  value={newPriority ?? 0}
                  aria-label="Priority"
                  onChange={(_: React.SyntheticEvent | null, val: number | null) => setNewPriority(val || null)}
                  sx={{ minWidth: 90 }}
                >
                  <Option value={0}>-</Option>
                  <Option value={1}><span style={{ color: '#c0392b', fontWeight: 'bold' }}>P0</span></Option>
                  <Option value={2}><span style={{ color: '#e67e22', fontWeight: 'bold' }}>P1</span></Option>
                  <Option value={3}><span style={{ color: '#7f8c8d', fontWeight: 'bold' }}>P2</span></Option>
                </Select>
                <Input
                  size="sm"
                  type="date"
                  value={newDueDate}
                  aria-label="Due date"
                  onChange={(e) => setNewDueDate(e.target.value)}
                  sx={{ width: 140 }}
                />
                <Button size="sm" onClick={() => void handleAdd()} disabled={newTitle.trim() === ''}>
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => {
                    setAddingNew(false);
                    setNewTitle('');
                    setNewPriority(null);
                    setNewDueDate('');
                    setAddError(null);
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            )}

            {addError && (
              <Typography level="body-sm" color="danger">
                {addError}
              </Typography>
            )}

            {sorted.length === 0 && !addingNew && (
              <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 2 }}>
                No todos yet. Click "Add Todo" to create one.
              </Typography>
            )}

            {sorted.map((todo) => (
              <PageRow key={todo.url} page={fromDBPage(todo)} hideTodoPill />
            ))}

          </Stack>
        </div>
      </div>
      <Inspector onSelectAll={() => {
        dispatch(clearSelectionAndSelectMulti({ urls: sorted.map((t) => ({ normalized: t.url } as any)) }));
      }} />
    </SplitPane>
  );
};

export default TodoView;
